import React from 'react';
import { format } from 'date-fns';
import { Post } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { PencilIcon, TrashIcon, SendIcon } from 'lucide-react';
import { Badge } from '../ui/badge';
import AuthenticatedImage from './AuthenticatedImage';

interface PostListProps {
  posts: Post[];
  onEditPost: (post: Post) => void;
  onDeletePost: (post: Post) => void;
  onPostNow: (post: Post) => void;
}

// Helper function to format date for scheduled posts
const formatPostDate = (scheduledTime: string | null) => {
  if (!scheduledTime) return null;
  return format(new Date(scheduledTime), "MMM d, yyyy 'at' h:mm a");
};

const PostList: React.FC<PostListProps> = ({ 
  posts, 
  onEditPost, 
  onDeletePost, 
  onPostNow 
}) => {
  if (!posts || posts.length === 0) {
    return <div className="text-center py-8 text-muted-foreground">No posts found</div>;
  }

  return (
    <div className="space-y-4">
      {posts.map((post) => (
        <Card key={post.id} className="w-full shadow-sm border-2 border-indigo-500">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <div>
                {post.platform && (
                  <div className="text-sm font-medium text-primary mb-1">
                    {post.platform}
                  </div>
                )}
                {post.scheduled_time && (
                  <div className="text-sm text-muted-foreground">
                    {formatPostDate(post.scheduled_time)}
                  </div>
                )}
              </div>
              <div>
                {post.status === 'scheduled' && (
                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200">
                    Scheduled
                  </Badge>
                )}
                {post.status === 'posted' && (
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100 border-green-200">
                    Posted
                  </Badge>
                )}
                {post.status === 'draft' && (
                  <Badge variant="outline">Draft</Badge>
                )}
                {post.status === 'failed' && (
                  <Badge variant="destructive" className="bg-red-100 text-red-800 hover:bg-red-100 border-red-200">
                    Failed
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {post.image_filename && (
              <div className="mb-4 flex justify-center bg-black/5 rounded-md p-2">
                <AuthenticatedImage 
                  filename={post.image_filename}
                  alt={`Post image for ${post.id}`} 
                  className="max-w-full h-auto rounded-md object-contain max-h-[300px]" 
                />
              </div>
            )}
            <div className="whitespace-pre-wrap text-base">
              {post.content}
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-3 pt-2 border-t">
            <Button
              variant="outline"
              onClick={() => onEditPost(post)}
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Edit
            </Button>
            {post.status !== 'posted' && (
              <Button
                variant="default"
                className="bg-green-600 hover:bg-green-700"
                onClick={() => onPostNow(post)}
              >
                <SendIcon className="h-4 w-4 mr-2" />
                Post Now
              </Button>
            )}
            <Button
              variant="destructive"
              onClick={() => onDeletePost(post)}
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
};

export default PostList; 